<script setup lang="ts">
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useFetctApplicableYearTariffCodes } from "@/hooks/use-tariff-codes"
import { cn } from '@/lib/utils'
import { useSunsetStore } from '@/store/use-sunset-store'
import { format } from 'date-fns'
import {
  AlertCircle,
  ArrowRight,
  CalendarIcon,
  Eye,
  Merge,
  RepeatIcon,
  Search,
  SunsetIcon,
  Trash2,
  UploadIcon,
  X
} from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import TariffCodeDetailDialog from './TariffCodeDetailDialog.vue'

const router = useRouter()

const applicableYear = ref('2025')
const isDialogOpen = ref(false)
const isSyncSunsetDialogOpen = ref(false)
const isSyncDisciplinesDialogOpen = ref(false)
const tariff = ref<any>(null)
const sunsetDate = ref<Date>()
const isSyncing = ref(false)

// Search state
const searchQuery = ref('')

// Get Store
const sunsetStore = useSunsetStore()

// Destructure state with storeToRefs
// const { members, tariffCodes, sunset } = storeToRefs(sunsetStore)

// Format date to YYYY/MM/DD
const formatDateToYYYYMMDD = (date: Date | undefined) => {
  if (!date) return ''
  return format(date, 'yyyy/MM/dd')
}

// Just assign the object
const handleViewTariffCodeDetail = (tariffData: any) => {
  console.log('Tariff Detail:', tariffData)
  tariff.value = tariffData
  isDialogOpen.value = true
}

// Check if tariff is selected
const isSelected = (tariffItem: any) => {
  return tariffCodeSunsetDataList.value.some(
    selected => selected.rate_id === tariffItem.rate_id
  )
}

// Toggle selection
const toggleSelection = (tariffItem: any, checked: boolean | string) => {
  const isChecked = checked === true || checked === 'true' || checked === 'indeterminate'
  
  if (isChecked) {
    // tariffCodeStore.setTariffCodeSunsetData(tariffItem)
  } else {
    // tariffCodeStore.removeTariffCodeSunsetDataById(tariffItem.rate_id)
  }
}

// Select all visible (filtered) tariffs
const selectAll = () => {
  filteredTariffs.value.forEach((tariffItem: any) => {
    if (!isSelected(tariffItem)) {
      // tariffCodeStore.setTariffCodeSunsetData(tariffItem)
    }
  })
}

// Deselect all - clear the store
const deselectAll = () => {
  tariffCodeStore.clearTariffCodeSunsetDataList()
}

// Toggle header checkbox
const toggleSelectAll = () => {
  if (allVisibleSelected.value) {
    // Deselect only visible items
    filteredTariffs.value.forEach((tariffItem: any) => {
      if (isSelected(tariffItem)) {
        tariffCodeStore.removeTariffCodeSunsetDataById(tariffItem.rate_id)
      }
    })
  } else {
    selectAll()
  }
}

// Open Sync Sunset Dialog
const openSyncSunsetDialog = () => {
  isSyncSunsetDialogOpen.value = true
}

// Open Sync Disciplines Dialog
const openSyncDisciplinesDialog = () => {
  isSyncDisciplinesDialogOpen.value = true
}

// Handle Navigate to Stage Rollover
const navigateToStageRollover = () => {
  isSyncDisciplinesDialogOpen.value = false
  router.push('/eligible-health-seekers')
}

// Handle Sync Sunset
const handleSyncSunset = async () => {
  if (!sunsetDate.value) {
    alert('Please select a sunset date')
    return
  }

  isSyncing.value = true
  
  const formattedDate = formatDateToYYYYMMDD(sunsetDate.value)
  
  console.log('Syncing sunset for:', {
    selectedTariffs: tariffCodeSunsetDataList.value,
    sunsetDate: formattedDate
  })

  router.push("/eligible-health-seekers")
}

const handleSuccess = () => {}

// Fetch tariff codes based on applicable year
const {
  data: fetchedData,
  isLoading, 
  isError, 
  error, 
  refetch 
} = useFetctApplicableYearTariffCodes(applicableYear.value)

// Use computed to reactively access the data
const tariffs = computed(() => fetchedData.value || [])

// Filtered tariffs based on search query
const filteredTariffs = computed(() => {
  if (!searchQuery.value.trim()) {
    return tariffs.value
  }

  const query = searchQuery.value.toLowerCase().trim()
  
  return tariffs.value.filter((item: any) => {
    const code = item.tariff_code?.code?.toLowerCase() || ''
    const description = item.tariff_code?.code_description?.toLowerCase() || ''
    const year = item.applicable_year?.toString() || ''
    
    return code.includes(query) || 
           description.includes(query) || 
           year.includes(query)
  })
})

// Clear search
const clearSearch = () => {
  searchQuery.value = ''
}

// Computed property for safe access
const hasResults = computed(() => filteredTariffs.value.length > 0)

// Computed for selected count
const selectedCount = computed(() => tariffCodeSunsetDataList.value.length)

// Computed to check if any tariffs are selected
const hasSelection = computed(() => selectedCount.value > 0)

// Check if all visible (filtered) items are selected
const allVisibleSelected = computed(() => {
  return filteredTariffs.value.length > 0 && 
    filteredTariffs.value.every((item: any) => isSelected(item))
})

// Check if some (but not all) visible items are selected
const someVisibleSelected = computed(() => {
  const selectedInVisible = filteredTariffs.value.filter((item: any) => isSelected(item)).length
  return selectedInVisible > 0 && selectedInVisible < filteredTariffs.value.length
})

</script>

<template>
  <div>
    <!-- Tariff Detail Dialog -->
    <TariffCodeDetailDialog 
      v-if="tariff"
      :tariff="tariff" 
      v-model:open="isDialogOpen"
    />

    <!-- Sync Sunset Dialog -->
    <Dialog v-model:open="isSyncSunsetDialogOpen">
      <DialogContent class="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle class="text-2xl flex items-center gap-2">
            <SunsetIcon class="h-6 w-6 text-amber-600" />
            Sync Sunset Date
          </DialogTitle>
          <DialogDescription>
            Set the sunset date for {{ selectedCount }} selected tariff code(s)
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-6 py-4">
          <!-- Selected Tariffs -->
          <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">
              Selected Tariff Codes ({{ selectedCount }})
            </h3>
            <div class="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
              <Badge 
                v-for="item in tariffCodeSunsetDataList" 
                :key="item.rate_id"
                variant="outline"
                class="font-mono"
              >
                {{ item.tariff_code.code }}
              </Badge>
            </div>
          </div>

          <!-- Date Picker -->
          <div class="space-y-2">
            <label class="text-sm font-semibold text-gray-700">
              Sunset Date <span class="text-red-500">*</span>
            </label>
            <Popover class="bg-slate-100">
              <PopoverTrigger as-child>
                <Button
                  variant="outline"
                  :class="cn(
                    'w-full justify-start text-left font-normal',
                    !sunsetDate && 'text-muted-foreground'
                  )"
                >
                  <CalendarIcon class="mr-2 h-4 w-4" />
                  <span v-if="sunsetDate">{{ formatDateToYYYYMMDD(sunsetDate) }}</span>
                  <span v-else>Pick a date</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent class="w-auto p-0 bg-white" align="start">
                <!-- @ts-ignore -->
                <Calendar v-model="sunsetDate" />
              </PopoverContent>
            </Popover>
            <p class="text-xs text-gray-500">
              Format: YYYY/MM/DD
              <span v-if="sunsetDate" class="text-green-400 font-normal ml-2">
                Selected: {{ formatDateToYYYYMMDD(sunsetDate) }}
              </span>
            </p>
          </div>

          <!-- Summary -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-start gap-3">
              <div class="bg-blue-100 rounded-full p-2">
                <AlertCircle class="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 class="text-sm font-semibold text-blue-400 mb-1">Summary</h4>
                <p class="text-sm text-blue-400">
                  You are about to sync <strong>{{ selectedCount }} tariff code(s)</strong>
                  <span v-if="sunsetDate"> with sunset date 
                    <strong>{{ formatDateToYYYYMMDD(sunsetDate) }}</strong>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            @click="isSyncSunsetDialogOpen = false"
            :disabled="isSyncing"
          >
            Cancel
          </Button>
          <Button 
            @click="handleSyncSunset"
            :disabled="!sunsetDate || isSyncing"
            class="bg-amber-600 hover:bg-amber-700"
          >
            <div v-if="isSyncing" class="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mb-4"></div>
            <template v-else>
              <SunsetIcon class="mr-2 h-4 w-4" />
              Sync Sunset
            </template>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Sync Disciplines Dialog -->
    <Dialog v-model:open="isSyncDisciplinesDialogOpen">
      <DialogContent class="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle class="text-2xl flex items-center gap-2">
            <RepeatIcon class="h-6 w-6 text-[#255f7a]" />
            Sync Disciplines
          </DialogTitle>
          <DialogDescription>
            Review {{ selectedCount }} selected tariff code(s) before syncing disciplines
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-6 py-4">
          <!-- Selected Tariffs -->
          <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">
              Selected Tariff Codes ({{ selectedCount }})
            </h3>
            <div class="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
              <Badge 
                v-for="item in tariffCodeSunsetDataList" 
                :key="item.rate_id"
                variant="outline"
                class="font-mono"
              >
                {{ item.tariff_code.code }}
              </Badge>
            </div>
          </div>

          <!-- Info Section -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-start gap-3">
              <div class="bg-blue-100 rounded-full p-2">
                <AlertCircle class="h-5 w-5 text-blue-600" />
              </div>
              <div class="flex-1">
                <h4 class="text-sm font-semibold text-blue-900 mb-2">What's Next?</h4>
                <p class="text-sm text-blue-700 mb-3">
                  You are about to sync disciplines for <strong>{{ selectedCount }} tariff code(s)</strong>.
                </p>
                <ul class="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li>Review the selected tariff codes above</li>
                  <li>Click "Next" to proceed</li>
                  <li>You can configure additional settings on the next page</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Statistics -->
          <div class="grid grid-cols-3 gap-4">
            <div class="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4 text-center">
              <p class="text-2xl font-bold text-purple-700">{{ selectedCount }}</p>
              <p class="text-xs text-purple-600 mt-1">Tariff Codes</p>
            </div>
            <div class="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4 text-center">
              <p class="text-2xl font-bold text-green-700">{{ applicableYear }}</p>
              <p class="text-xs text-green-600 mt-1">Applicable Year</p>
            </div>
            <div class="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4 text-center">
              <p class="text-2xl font-bold text-orange-700">Ready</p>
              <p class="text-xs text-orange-600 mt-1">Status</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            @click="isSyncDisciplinesDialogOpen = false"
          >
            Cancel
          </Button>
          <Button 
            @click="navigateToStageRollover"
            class="bg-[#255f7a] hover:bg-[#1d4a5f] text-white"
          >
            Next
            <ArrowRight class="ml-2 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Loading tariff codes...</p>
      </div>
    </div>
    
    <!-- Error State -->
    <Card v-else-if="isError" class="border-red-200 bg-red-50 m-8">
      <CardContent class="flex flex-col items-center justify-center py-12">
        <AlertCircle class="h-12 w-12 text-red-600 mb-4" />
        <p class="text-red-600 font-semibold mb-2">Error loading tariff codes</p>
        <p class="text-red-500 text-sm">{{ error?.message }}</p>
        <Button 
          @click="refetch()" 
          variant="outline"
          class="mt-4 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
        >
          Retry
        </Button>
      </CardContent>
    </Card>
    
    <!-- Table -->
    <div v-else-if="tariffs.length > 0" class="rounded-md border m-8">
      <div class="p-3 border-b-2">
        <div class="flex items-center mb-4">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">
              Tariff Codes - 
              <span class="text-gray-400">{{ tariffs.length }}</span>
            </h2>
            <p class="text-gray-600">
              Manage and view tariff codes
              <span v-if="selectedCount > 0" class="text-green-600 font-semibold ml-2">
                ({{ selectedCount }} selected)
              </span>
            </p>
          </div>
          <div class="ml-auto flex items-center space-x-3">
            <!-- Selection Actions -->
            <Button 
              v-if="selectedCount > 0"
              variant="outline"
              class="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
              @click="deselectAll()"
            >
              <Trash2 class="h-4 w-4" />
              Clear Selection ({{ selectedCount }})
            </Button>

            <Button 
              v-if="!allVisibleSelected && filteredTariffs.length > 0"
              variant="outline"
              class="flex items-center gap-2"
              @click="selectAll()"
            >
              Select All {{ searchQuery ? 'Filtered' : '' }}
            </Button>

            <Button 
              variant="outline"
              class="flex items-center gap-3 rounded-md justify-between bg-amber-500 text-white p-2 text-md hover:bg-green-800 hover:text-white" 
              @click="$router.push('/admin')"
            >
              Merge HBC <Merge />
            </Button>

            <!-- Sync Sunset - Opens Dialog -->
            <Button 
              variant="outline"
              :disabled="!hasSelection"
              :class="[
                'flex items-center gap-3 rounded-md justify-between p-2 text-md',
                hasSelection 
                  ? 'bg-amber-700 text-white hover:bg-green-800 hover:text-white' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              ]"
              @click="hasSelection ? openSyncSunsetDialog() : null"
            >
              Sync Sunset <SunsetIcon /> 
            </Button>

            <!-- Sync Disciplines - Opens Dialog -->
            <Button
              variant="outline" 
              :disabled="!hasSelection"
              :class="[
                'flex items-center gap-3 rounded-md justify-between p-2 text-md font-medium hover:shadow-lg',
                hasSelection 
                  ? 'bg-[#255f7a] text-white hover:bg-green-800 hover:text-white' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              ]"
              @click="hasSelection ? openSyncDisciplinesDialog() : null"
            > 
              Sync Disciplines <RepeatIcon />
            </Button>
            
            <Button 
              variant="outline" 
              class="flex items-center gap-3 rounded-md bg-slate-800 text-white p-2 text-md font-medium hover:bg-green-800 hover:text-white hover:shadow-lg"
              @click="handleSuccess()"
            >
              Upload <UploadIcon class="h-4 w-4"/>
            </Button>
          </div>
        </div>

        <!-- Search Bar -->
        <div class="relative max-w-md">
          <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            v-model="searchQuery"
            placeholder="Search by tariff code, description, or year..."
            class="pl-10 pr-10"
          />
          <button
            v-if="searchQuery"
            @click="clearSearch"
            class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X class="h-4 w-4" />
          </button>
        </div>
        <p v-if="searchQuery" class="text-sm text-gray-600 mt-2">
          Found {{ filteredTariffs.length }} result(s) matching "{{ searchQuery }}"
        </p>
      </div>
      
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead class="w-12">
              <Checkbox 
                :checked="allVisibleSelected"
                :indeterminate="someVisibleSelected"
                @click="toggleSelectAll"
              />
            </TableHead>
            <TableHead>Tariff Code</TableHead>
            <TableHead>Description</TableHead>
            <TableHead class="text-center">Year</TableHead>
            <TableHead class="text-center">Limits</TableHead>
            <TableHead class="text-center">Status</TableHead>
            <TableHead class="text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow 
            v-for="tariffItem in filteredTariffs" 
            :key="tariffItem.rate_id"
            :class="[
              isSelected(tariffItem) ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
            ]"
          >
            <TableCell>             
              <Checkbox 
                :checked="isSelected(tariffItem)"
                @click="() => toggleSelection(tariffItem, !isSelected(tariffItem))"
              />
            </TableCell>
            
            <TableCell>
              <Badge 
                variant="outline" 
                :class="[
                  'font-mono',
                  isSelected(tariffItem) ? 'border-green-300 text-green-600' : ''
                ]"
              >
                {{ tariffItem.tariff_code.code }}
              </Badge>
            </TableCell>

            <TableCell class="max-w-md">
              <p class="text-sm truncate" :title="tariffItem.tariff_code.code_description">
                {{ tariffItem.tariff_code.code_description }}
              </p>
            </TableCell>

            <TableCell class="text-center">
              <Badge variant="secondary">{{ tariffItem.applicable_year }}</Badge>
            </TableCell>
            
            <TableCell class="text-center">
              {{ tariffItem.applicable_limits }}
            </TableCell>
            
            <TableCell class="text-center">
              <Badge :variant="tariffItem.active ? 'default' : 'secondary'">
                {{ tariffItem.active ? 'Active' : 'Inactive' }}
              </Badge>
            </TableCell>
            
            <TableCell class="text-center">
              <Button variant="ghost" size="lg" @click="handleViewTariffCodeDetail(tariffItem)">
                <Eye class="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
          
          <!-- No Results Row -->
          <TableRow v-if="filteredTariffs.length === 0">
            <TableCell colspan="7" class="text-center py-12">
              <div class="flex flex-col items-center">
                <Search class="h-12 w-12 text-gray-300 mb-3" />
                <p class="text-gray-500 font-medium">No tariff codes found</p>
                <p class="text-sm text-gray-400 mt-1">Try adjusting your search</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  @click="clearSearch"
                  class="mt-3"
                >
                  Clear Search
                </Button>
              </div>
            </TableCell>
          </TableRow>
        </TableBody>  
      </Table>
    </div>
    
    <!-- Empty State -->
    <Card v-else class="m-8">
      <CardContent class="flex flex-col items-center justify-center py-12">
        <AlertCircle class="h-12 w-12 text-gray-400 mb-4" />
        <p class="text-gray-500">No tariff codes available</p>
      </CardContent>
    </Card>
  </div>
</template>